import { View } from "react-native";
import { Button } from "react-native-paper";
import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { DrawerParamList } from "../../types/navigationParams";
import DynamicDisplay from "../../components/DynamicDisplay";
import { DrawerScreenProps } from "@react-navigation/drawer";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../redux/store";
import {
  listenCompanyDocuments,
  listenDocuments,
  QueryOptions,
  getCollectionCount,
  getCompanyDocuments,
  getDocuments,
} from "../../services/firestoreServices";
import { AuthState } from "../../redux/slices/authSlice";
import FilterBar from "../../components/FilterBar";
import WeekNavigator from "../../components/WeekNavigator";
import { Filter, Filters } from "../../config/companies/config";
import { useWeekNavigation } from "../../hooks/useWeekNavigation";
import { alert } from "../../utils/alert";
import {
  buildCsvContent,
  downloadCsvFile,
  formatExportValue,
} from "../../utils/csvExport";
import { getComparableValue } from "../../utils/tableSort";

type Props = DrawerScreenProps<DrawerParamList, "ListScreen">;

export const getValue = (obj: Record<string, any>, path: string): any =>
  path.split(".").reduce((acc: any, key: string) => acc?.[key], obj);

const safeSearchString = (value: unknown): string => {
  if (value === null || typeof value === "undefined") return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const getFilterValue = (item: Record<string, any>, field: string) => {
  const directValue = field.includes(".") ? getValue(item, field) : item[field];

  if (directValue !== null && typeof directValue !== "undefined") {
    return directValue;
  }

  if (field === "block") {
    return getValue(item, "tenantCompany.block");
  }

  return directValue;
};

const hasFilterValue = (filter: Filter) => {
  if (filter.type === "date") {
    return Boolean(filter.value?.startDate && filter.value?.endDate);
  }

  return Boolean(filter.value);
};

const CSV_EXCLUDED_FIELDS = new Set([
  "addedBy",
  "attachments",
  "boardOrder",
  "chat",
  "chatStats",
  "checklist",
  "companyId",
  "updatedBy",
  "createdById",
  "index",
  "id",
  "isArchived",
  "isDeleted",
  "type",
  "warehouseItems",
]);

export default function ListScreen({ navigation, route }: Props) {
  const state = useAppSelector((state) => state.auth);
  const timestamp = useAppSelector((state) => state.auth.timestamp);
  const config = useAppSelector((state) => state.auth.config);
  const params = config.screens.find((s) => s.name === route.name)!;
  const {
    collection: collectionName,
    where,
    onlyCompany = true,
    presentation = "list",
    showAdd,
    loadAll = false,
    orderBy,
    filters: initialFilters,
    weekNavigation,
    tableResponsive,
    csvExport,
  } = params;

  const collection = state.config.database.find(
    (item) => item.name === collectionName.split(",")[0],
  )!;

  const [rawData, setRawData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [activeFilters, setActiveFilters] = useState<Filters>(
    initialFilters ? [...initialFilters] : [],
  );
  const [collectionDataCache, setCollectionDataCache] = useState<
    Record<string, any[]>
  >({});

  useEffect(() => {
    setActiveFilters(initialFilters ? [...initialFilters] : []);
  }, [route.name]);

  useEffect(() => {
    if (!initialFilters) return;

    const collectionsToFetch = initialFilters
      .filter((f) => f.type === "dropdown" && f.optionsFromCollection)
      .map((f) => f.optionsFromCollection as string);

    if (collectionsToFetch.length === 0) return;

    collectionsToFetch.forEach((colName) => {
      setCollectionDataCache((prev) => {
        if (prev[colName]) {
          return prev;
        }

        const fetchFunction = onlyCompany ? getCompanyDocuments : getDocuments;
        fetchFunction(colName)
          .then((docs) => {
            setCollectionDataCache((current) => ({
              ...current,
              [colName]: docs,
            }));
          })
          .catch((error) => {
            console.error(
              `Failed to fetch options for collection ${colName}:`,
              error,
            );
          });

        return prev;
      });
    });
  }, [initialFilters, route.name, onlyCompany]);

  const [queryLimit, setQueryLimit] = useState<number | undefined>(
    loadAll ? undefined : 20,
  );
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const { t } = useTranslation();

  const canAdd = showAdd === undefined || showAdd.includes(state.user.role);
  const canExportCsv = !!csvExport?.enabled;
  const weekField = weekNavigation?.field;
  const {
    currentWeek,
    weekDates,
    goToPreviousWeek,
    goToNextWeek,
    getWeekWhere,
  } = useWeekNavigation();

  const getResolvedWhere = () => {
    const resolvedWhere: QueryOptions["where"] = [];

    if (where) {
      resolvedWhere.push(
        ...where.map((item) => {
          const stateValue = state[item.from as keyof AuthState];
          const filterValue = item.from
            ? getValue(stateValue as Record<string, any>, String(item.value))
            : item.value;

          return {
            field: item.field,
            operator: item.operator,
            value: filterValue,
          };
        }),
      );
    }

    resolvedWhere.push(...getWeekWhere(weekField));

    return resolvedWhere;
  };

  useLayoutEffect(() => {
    navigation.setOptions({
      headerRight: (props) =>
        canAdd &&
        collection && (
          <Button
            testID={"add-data"}
            onPress={() =>
              navigation.navigate("FormScreen", {
                item: undefined,
                fields: collection.fields,
                collection: collection.name,
              })
            }
            {...props}
          >
            {t("Add")}
          </Button>
        ),
    });
  }, [canAdd, collection, collection.fields, collection.name, navigation, t]);

  // Fetch total count
  useEffect(() => {
    const collectionNames = collectionName.split(",");
    const options: QueryOptions = {
      where: getResolvedWhere(),
      onlyCompany,
    };

    Promise.all(
      collectionNames.map((colName) => getCollectionCount(colName, options)),
    )
      .then((counts) => {
        const sum = counts.reduce((acc, c) => acc + c, 0);
        setTotalCount(sum);
      })
      .catch((error) => {
        console.error("Failed to get collection count:", error);
        setTotalCount(null);
      });
  }, [
    collectionName,
    onlyCompany,
    state,
    where,
    weekField,
    currentWeek,
    timestamp,
  ]);

  useEffect(() => {
    const collectionNames = collectionName.split(",");
    const listenFunction = onlyCompany
      ? listenCompanyDocuments
      : listenDocuments;
    const options: QueryOptions = {
      where: getResolvedWhere(),
    };

    // orderBy is expected to be array of { field, direction }
    const optionsWithOrder: QueryOptions = {
      ...options,
      orderBy: orderBy as any,
      limit: queryLimit,
    };

    const latestDataMap: Record<string, any[]> = {};
    const unsubscribes = collectionNames.map((colName) => {
      return listenFunction(
        colName,
        (data) => {
          const processedData = data.map((item) => ({
            ...item,
            _collection: colName,
          }));

          latestDataMap[colName] = !collection.visibleAt
            ? processedData
            : processedData.filter((item) => {
                if (item.createdById === state.user.uid) {
                  return true;
                }

                const visibleAt = item.visibleAt;
                if (!visibleAt) {
                  return true;
                }

                const visibleAtDate = visibleAt?.toDate
                  ? visibleAt.toDate()
                  : new Date(visibleAt);
                visibleAtDate.setHours(0, 0, 0, 0);

                const today = new Date();
                today.setHours(0, 0, 0, 0);

                return visibleAtDate <= today;
              });

          // Merge all data from all collections
          const allMergedData: any[] = [];
          collectionNames.forEach((name) => {
            const list = latestDataMap[name] || [];
            allMergedData.push(...list);
          });

          // Sort merged data manually because sorting across collections cannot be done by Firestore
          if (orderBy && orderBy.length > 0) {
            allMergedData.sort((a, b) => {
              for (const { field, direction } of orderBy) {
                const valA = getComparableValue(a[field]);
                const valB = getComparableValue(b[field]);
                if (valA === valB) continue;
                const comp = valA < valB ? -1 : 1;
                return direction === "desc" ? -comp : comp;
              }
              return 0;
            });
          }

          const limitedData =
            queryLimit !== undefined
              ? allMergedData.slice(0, queryLimit)
              : allMergedData;

          setRawData(limitedData);
          if (activeFilters.length > 0) {
            applyFilters(limitedData, activeFilters);
          } else {
            setFilteredData(limitedData);
          }
        },
        optionsWithOrder,
      );
    });

    return () => {
      unsubscribes.forEach((unsub) => unsub());
    };
  }, [
    collectionName,
    onlyCompany,
    state,
    where,
    weekField,
    currentWeek,
    activeFilters,
    queryLimit,
    timestamp,
  ]);

  const handleLoadMore = (increment: number) => {
    if (queryLimit === undefined) return; // Already loading all
    setQueryLimit(queryLimit + increment);
  };

  const handleLoadAll = () => {
    setQueryLimit(undefined); // Remove limit to load all
  };

  useEffect(() => {
    setQueryLimit(loadAll ? undefined : 20);
  }, [loadAll]);

  const applyDateFilter = (data: any[], filter: Filter) => {
    if (!filter.value) {
      return data;
    }

    const { startDate, endDate } = filter.value;
    const inclusiveEndDate = new Date(endDate);
    inclusiveEndDate.setHours(23, 59, 59, 999);

    return data.filter((item) => {
      const itemDate = item[filter.field];
      if (!itemDate) return false;

      // Проверяем, является ли дата Timestamp из Firestore
      const timestamp = itemDate?.toDate
        ? itemDate.toDate()
        : new Date(itemDate);

      return timestamp >= startDate && timestamp <= inclusiveEndDate;
    });
  };

  const applySearchFilter = (data: any[], newFilter: Filter) => {
    const searchTerm = newFilter.value;

    if (!searchTerm) {
      return data;
    }

    const needle = String(searchTerm).toLowerCase();

    if (newFilter.searchTermKeys) {
      const searchTermKeys = newFilter.searchTermKeys;
      return data.filter((item) => {
        const concatenatedFields = searchTermKeys.reduce(
          (resultString, fieldName) => {
            const nextValue =
              typeof fieldName === "string" && fieldName.includes(".")
                ? getValue(item, fieldName)
                : item[fieldName];
            return resultString + " " + safeSearchString(nextValue);
          },
          "",
        );

        // Also include the main filter field (supports nested paths)
        const mainFieldValue = newFilter.field.includes(".")
          ? getValue(item, newFilter.field)
          : item[newFilter.field];
        const haystack = (
          safeSearchString(mainFieldValue) +
          " " +
          concatenatedFields
        ).toLowerCase();

        return haystack.includes(needle);
      });
    }

    throw new Error(
      `searchTermKeys не определён для фильтра с полем "${newFilter.field}". Проверь конфигурацию фильтра.`,
    );
  };

  const applyDropdownFilter = (data: any[], filter: Filter) => {
    if (!filter.value) {
      return data;
    }

    return data.filter((item) => {
      const fieldValue = getFilterValue(item, filter.field);

      return formatExportValue(fieldValue) === String(filter.value);
    });
  };

  const applyFilters = (data: any[], filters: Filters) => {
    let filtered = [...data];

    filters.forEach((filter) => {
      switch (filter.type) {
        case "date":
          filtered = applyDateFilter(filtered, filter);
          break;
        case "search":
          filtered = applySearchFilter(filtered, filter);
          break;
        case "dropdown":
          filtered = applyDropdownFilter(filtered, filter);
          break;
        default:
          console.warn(`Unknown filter type: ${filter.type}`);
      }
    });

    setFilteredData(filtered);
  };

  const handleFilterChange = (updatedFilters: Filters) => {
    let finalFilters = updatedFilters;

    updatedFilters.forEach((filter) => {
      if (filter.dependsOn && filter.value) {
        const parentField = filter.dependsOn.field;
        const parentFilter = updatedFilters.find(
          (p) => p.field === parentField,
        );
        const oldParentFilter = activeFilters.find(
          (p) => p.field === parentField,
        );

        if (
          parentFilter &&
          oldParentFilter &&
          parentFilter.value !== oldParentFilter.value
        ) {
          finalFilters = finalFilters.map((f) => {
            if (f.field === filter.field) {
              return { ...f, value: "" };
            }
            return f;
          });
        }
      }
    });

    setActiveFilters(finalFilters);
    applyFilters(rawData, finalFilters);
  };

  const filtersWithDataOptions = useMemo<Filters | undefined>(() => {
    if (!initialFilters) return undefined;

    return initialFilters.map((filter) => {
      if (filter.type !== "dropdown") {
        return filter;
      }

      if (filter.optionsFromCollection) {
        const cachedDocs =
          collectionDataCache[filter.optionsFromCollection] || [];
        const optionSet = new Set<string>();

        cachedDocs.forEach((item) => {
          if (filter.dependsOn) {
            const parentField = filter.dependsOn.field;
            const parentFilter = activeFilters.find(
              (f) => f.field === parentField,
            );
            if (parentFilter && parentFilter.value) {
              const itemParentValue = getFilterValue(item, parentField);
              if (
                formatExportValue(itemParentValue) !==
                String(parentFilter.value)
              ) {
                return;
              }
            }
          }

          const key = filter.optionsCollectionKey || "name";
          const value = getFilterValue(item, key);
          const formattedValue = formatExportValue(value).trim();
          if (formattedValue) {
            optionSet.add(formattedValue);
          }
        });

        return {
          ...filter,
          options: Array.from(optionSet).sort((a, b) => a.localeCompare(b)),
        };
      }
      return filter;
    });
  }, [initialFilters, rawData, activeFilters, collectionDataCache]);

  const buildCsv = (data: any[]) => {
    const fieldNames = new Set(
      collection.fields
        .map((field) => field.name)
        .filter((name) => !CSV_EXCLUDED_FIELDS.has(name)),
    );
    data.forEach((item) => {
      Object.keys(item).forEach((key) => {
        if (!fieldNames.has(key) && !CSV_EXCLUDED_FIELDS.has(key)) {
          fieldNames.add(key);
        }
      });
    });

    const fieldsByName = new Map(
      collection.fields.map((field) => [field.name, field]),
    );
    const exportFields = Array.from(fieldNames).map((name) => ({
      name,
      label: fieldsByName.get(name)?.label || name,
    }));

    return buildCsvContent(
      exportFields.map((field) => ({
        header: t(field.label),
        value: (item: any) =>
          field.name.includes(".")
            ? getValue(item, field.name)
            : item[field.name],
      })),
      data,
    );
  };

  const handleCsvExport = async () => {
    try {
      const fetchFunction = onlyCompany ? getCompanyDocuments : getDocuments;
      const options: QueryOptions = {
        where: getResolvedWhere(),
        orderBy: orderBy as any,
      };

      const collectionNames = collectionName.split(",");
      const allRawDocs: any[] = [];
      for (const colName of collectionNames) {
        const docs = await fetchFunction(colName, options);
        const processedDocs = docs.map((item: any) => ({
          ...item,
          _collection: colName,
        }));
        allRawDocs.push(...processedDocs);
      }
      let exportData = allRawDocs;

      // Sort merged data manually for CSV
      if (orderBy && orderBy.length > 0) {
        exportData.sort((a, b) => {
          for (const { field, direction } of orderBy) {
            const valA = getComparableValue(a[field]);
            const valB = getComparableValue(b[field]);
            if (valA === valB) continue;
            const comp = valA < valB ? -1 : 1;
            return direction === "desc" ? -comp : comp;
          }
          return 0;
        });
      }

      if (activeFilters.length > 0) {
        activeFilters.forEach((filter) => {
          switch (filter.type) {
            case "date":
              exportData = applyDateFilter(exportData, filter);
              break;
            case "search":
              exportData = applySearchFilter(exportData, filter);
              break;
            case "dropdown":
              exportData = applyDropdownFilter(exportData, filter);
              break;
            default:
              break;
          }
        });
      }

      if (exportData.length === 0) {
        alert(t("No data"), t("No records to export"));
        return;
      }

      const csv = buildCsv(exportData);
      const dateSuffix = new Date().toISOString().slice(0, 10);
      const fileName = `${csvExport?.fileName || collectionName}-${dateSuffix}`;
      await downloadCsvFile(fileName, csv);
    } catch (error) {
      console.error("Failed to export CSV:", error);
      alert(t("Error"), t("Failed to export CSV"));
    }
  };

  if (!collection) {
    return null;
  }

  const effectiveTotalCount =
    queryLimit === undefined && activeFilters.some(hasFilterValue)
      ? filteredData.length
      : totalCount;

  return (
    <View style={{ flex: 1 }}>
      {weekField && (
        <WeekNavigator
          weekDates={weekDates}
          onPrevious={goToPreviousWeek}
          onNext={goToNextWeek}
        />
      )}

      {filtersWithDataOptions && filtersWithDataOptions.length > 0 && (
        <FilterBar
          filters={filtersWithDataOptions}
          onFilterChange={handleFilterChange}
          activeFilters={activeFilters}
        />
      )}

      {canExportCsv && (
        <View style={{ paddingHorizontal: 16, paddingBottom: 8 }}>
          <Button mode="contained" icon="download" onPress={handleCsvExport}>
            {t("Export CSV")}
          </Button>
        </View>
      )}

      <DynamicDisplay
        presentation={presentation}
        collection={collection}
        fields={collection.fields}
        data={filteredData}
        orderBy={orderBy}
        tableResponsive={tableResponsive}
        onLoadMore={handleLoadMore}
        onLoadAll={handleLoadAll}
        hasLimit={queryLimit !== undefined}
        totalCount={effectiveTotalCount}
      />
    </View>
  );
}
